<template>
  <v-container class="event-selector-container">
    <v-row dense row wrap class="event-selector-row">
      <v-col
        @click="toggleExpand"
        class="col-10"
        :class="{
          clickable: expandable,
        }"
      >
        <img v-if="imgSrc" :src="imgSrc" :alt="topic.plural" />
        <span class="ml-4 name-label application-heading"
          >{{ topic.plural
          }}<span v-if="topicFilterCounts" class="hyp-total"
            >{{ formatFilterCount(topicFilterCounts, topic) }}
          </span>
        </span>
      </v-col>
      <v-col class="col-2">
        <v-switch
          v-model="switchEnabled"
          @click="toggleSwitch({ mapAll: true })"
          class="event-input-toggle pa-0 ma-0 mt-1"
          solo
          item-text="display"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row dense row wrap class="mt-3">
      <v-col class="col-12">
        <ul v-if="this.expanded" class="category-list">
          <li :key="index" v-for="(item, index) in availableItems">
            <v-checkbox
              v-model="selectedItems"
              :value="item.apiName"
              @click="handleCheckboxChange(item)"
              class="category-input"
              dense
            >
              <template v-slot:label>
                <span class="application-heading">{{ item.plural }}</span>
                <span class="hyp-total">{{
                  formatFilterCount(topicFilterCounts, item)
                }}</span>
              </template>
            </v-checkbox>
          </li>
        </ul>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashFind from "lodash/find";
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";

import { ICONS } from "@/common/constants/icons";
import { getTopicsArray, getTopicKey } from "@/services/events/topics.service";
import { formatBigNumber } from "@/common/basic.utilities";

const EMITTED_ACTIONS = {
  CHANGED: "changed",
  CHANGED_LIVE_SEARCH: "changed-live-search",
  EXPANDED: "expanded",
};

export default {
  name: "EventPicker",
  props: {
    topic: {
      topic: Object,
      required: true,
    },
    selectedSubtopics: {
      type: Array,
    },
    reset: {
      type: String,
    },
    total: {
      type: Number,
    },
    topicFilterCounts: {
      type: Object,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    mapAll: {
      type: Object,
    },
  },
  data() {
    return {
      switchEnabled: false,
      availableItems: null,
      selectedItems: [],
    };
  },
  computed: {
    imgSrc() {
      let imgSrc, src;
      try {
        if (!ICONS) {
          return null;
        }
        src = ICONS.types[this.topic.type].active;
        if (this.switchEnabled) {
          src = ICONS.types[this.topic.type].inactive;
        }
        imgSrc = require(`@/assets/images${src}`);
      } catch (err) {
        src = ICONS.types["events_general"].active;
        if (this.switchEnabled) {
          src = ICONS.types["events_general"].inactive;
        }
        imgSrc = src;
      }
      return imgSrc;
    },
    expandable() {
      return lodashGet(this, "topic.subtopics.length") > 0;
    },
    hasSubtopics() {
      return (
        this.topic && this.topic.subtopics && this.topic.subtopics.length > 0
      );
    },
  },
  mounted() {
    if (
      this.topic.subtopics &&
      Array.isArray(this.topic.subtopics) &&
      this.topic.subtopics.length
    ) {
      this.availableItems = this.topic.subtopics;
    }
    this.initialize();
  },
  methods: {
    initialize() {
      this.setSwitchState();
      this.selectedItems = [];
      this.selectedSubtopics.forEach((item) => {
        if (!this.selectedItems.includes(item)) {
          this.selectedItems.push(item);
        }
      });
      if (this.selectedItems.length > 0 || this.topic.selected) {
        this.toggleExpand();
      }
    },
    setSwitchState() {
      let isSwitchEnabled = this.topic.selected;
      if (this.selectedSubtopics.length) {
        isSwitchEnabled = true;
      }
      this.switchEnabled = isSwitchEnabled;
    },
    toggleExpand(event, opts) {
      if (this.expandable) {
        //don't close an open section you're turning on
        if (this.switchEnabled && this.expanded && !event) {
          return;
        }

        //don't open a collapsed section you're turning off
        if (!this.switchEnabled && !this.expanded && !event) {
          return;
        }

        let expanded = !this.expanded;
        this.$emit(EMITTED_ACTIONS.EXPANDED, {
          expanded: expanded,
          topic: this.topic.type,
        });
      }
    },
    toggleSwitch(opts = {}) {
      if (!opts.skipExpand) {
        this.toggleExpand(null, opts);
      }
      let selected = [];
      if (
        this.switchEnabled &&
        this.availableItems &&
        Array.isArray(this.availableItems) &&
        this.availableItems.length
      ) {
        this.availableItems.map((item) => {
          if (
            this.selectedSubtopics.length < 1 ||
            this.selectedSubtopics.includes(item.apiName) ||
            opts.mapAll
          ) {
            selected.push(item.apiName);
          }
        });
      }
      this.selectedItems = selected;
      const TOPICS_ARRAY = getTopicsArray();
      const t = lodashFind(TOPICS_ARRAY, { type: this.topic.type });
      if (t && t.subtopics && t.subtopics.length) {
        for (let i = 0; i < t.subtopics.length; i++) {
          const sTopic = t.subtopics[i];
          const pType = getTopicKey(sTopic.parentIds[0]);
          const parent = lodashFind(TOPICS_ARRAY, { type: pType });
          lodashDebounce(() => {
            this.$emit(EMITTED_ACTIONS.CHANGED, {
              topic: parent,
              subtopic: sTopic,
              subtopics: [],
              allSubtopicsSelected:
                this.selectedItems.length === this.availableItems
                  ? this.availableItems.length
                  : false,
              enabled: this.selectedItems.includes(sTopic.apiName),
            });
          }, 500)();
        }
      }

      //Only map primary topic if it has no subtopics
      if (!this.hasSubtopics) {
        this.$emit(EMITTED_ACTIONS.CHANGED, {
          topic: this.topic,
          subtopic: null,
          subtopics: [],
          enabled: this.switchEnabled,
        });
      }

      this.$emit(EMITTED_ACTIONS.CHANGED_LIVE_SEARCH, {
        topic: this.topic,
        subtopic: null,
        subtopics: this.selectedItems,
        allSubtopicsSelected:
          this.selectedItems &&
          this.availableItems &&
          this.selectedItems.length === this.availableItems.length,
        enabled: this.switchEnabled,
      });
    },
    handleCheckboxChange(item) {
      this.switchEnabled = !!this.selectedItems.length;

      let event = {
        topic: this.topic,
        subtopic: item || null,
        subtopics: this.selectedItems,
        allSubtopicsSelected:
          this.selectedItems.length === this.availableItems.length,
        enabled: this.selectedItems.includes(item.apiName),
      };

      this.$emit(EMITTED_ACTIONS.CHANGED, event);
      this.$emit(EMITTED_ACTIONS.CHANGED_LIVE_SEARCH, event);
    },
    formatFilterCount(topicFilterCounts, topic) {
      let count = lodashGet(topicFilterCounts, topic.apiName);
      if (count || count === 0) {
        return `(${formatBigNumber(count)})`;
      }
    },
  },
  watch: {
    reset: {
      handler() {
        this.switchEnabled = false;
        this.toggleSwitch({ skipExpand: false });
      },
      deep: true,
    },
    mapAll: {
      handler(val) {
        if (val) {
          this.switchEnabled = val.enabled;
          this.toggleSwitch({ skipExpand: false, mapAll: val.enabled });
        }
      },
      deep: true,
    },
    topic: {
      handler() {
        this.switchEnabled = this.topic.selected;
        this.toggleSwitch();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.event-selector-container {
  padding: 0 0 5px 0 !important;
}
.event-selector-row {
  height: 20px;
}
.name-label {
  color: #1c1c1c;
  font-size: 14px !important;
  margin: 0 0 0 5px !important;
  position: relative;
  top: -8px;
}
.topic-image {
  height: 20px;
  width: 20px;
  margin: 0 0 2px 0;
  vertical-align: middle;
}
.category-list {
  list-style-type: none;
  padding: 0 0 0 16px;
}
.category-input {
  margin: 4px;
}
.category-input >>> .v-label {
  color: #1c1c1c;
  font-size: 13px !important;
}
.category-input >>> .v-messages {
  min-height: 0 !important;
}
.category-input >>> .v-input__slot {
  margin: 0 !important;
}
.hyp-total {
  font-size: 12px;
  position: relative;
  top: -1px;
  padding-left: 5px;
}
.clickable {
  cursor: pointer;
}
</style>
